import { useState, useMemo } from "react";
import { Button, IconButton, Chip } from "@mui/material";
import { MoreVert, ChevronLeft, ChevronRight, ConfirmationNumber } from "@mui/icons-material";
import { TICKET_STATUS } from "../../../../../hooks/event-hooks/useEvent";
import Menus from "../../../../../components/Dialog/Menus";
import { ccyFormat } from "../../../../../utils/currencyFormatter";
import { H6, Body2, Body3, Body1 } from "../../../../../themes/typography";

function TicketInventory({
    data = [],
    onNewTicketTypeAdd = () => { },
    menusList = [],
    onElipsisClick = () => { },
    onElipsisClose = () => { },
    anchorEl = null,
    open = false,
}) {
    const [currentPage, setCurrentPage] = useState(1);
    const ticketsPerPage = 4;

    // 🧮 Calculate total pages
    const totalPages = Math.ceil(data.length / ticketsPerPage) || 1;

    // 🧩 Slice data for current page
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * ticketsPerPage;
        const endIndex = startIndex + ticketsPerPage;
        return data.slice(startIndex, endIndex);
    }, [data, currentPage]);

    // ⬅️ Navigate to previous page
    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    // ➡️ Navigate to next page
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    return (
        <div className="ticket-inventory-container">
            {/* Header */}
            <div className="ticket-inventory-header">
                <H6 fontWeight={600}>Ticket Inventory</H6>
                <Button variant="mainSm" onClick={onNewTicketTypeAdd}>
                    Add Ticket Type
                </Button>
            </div>

            {/* Table Header */}
            <div className="ticket-inventory-table">
                <div className="ticket-inventory-table-header">
                    <Body2>Ticket Name</Body2>
                    <Body2>Price</Body2>
                    <Body2>Sold</Body2>
                    <Body2>Progress</Body2>
                    <Body2>Revenue</Body2>
                    <Body2>Status</Body2>
                    <Body2>Actions</Body2>
                </div>

                {/* No Tickets */}
                {data.length === 0 && (
                    <div className="no-system-alerts">
                        <div className="no-system-alerts-icon">
                            <ConfirmationNumber sx={{ fontSize: "3rem" }} color="inherit" />
                        </div>
                        <Body1 fontWeight={400}>
                            No tickets have been created yet.
                        </Body1>
                        <Body2 color="var(--secondary-color)" fontWeight={350}>
                            Create ticket types to start selling tickets for your event.
                        </Body2>
                    </div>
                )}

                {/* Ticket Rows */}
                {paginatedData.map((ticket, index) => {
                    const progressPercent =
                        (ticket.ticket_sold / ticket.available_quantity) * 100 || 0;

                    return (
                        <div className="ticket-inventory-table-row" key={index}>
                            <Body2 fontWeight={500}>{ticket.ticket_name}</Body2>
                            <Body2>
                                {Number(ticket.price) === 0
                                    ? "Free"
                                    : `${ccyFormat(ticket.price)}`}
                            </Body2>
                            <Body2>
                                {ticket.ticket_sold} / {ticket.available_quantity}
                            </Body2>

                            {/* Progress Bar */}
                            <div className="ticket-inventory-progress">
                                <div className="ticket-inventory-progress-bar">
                                    <div
                                        className="ticket-inventory-progress-fill"
                                        style={{ width: `${progressPercent}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Revenue Placeholder */}
                            <Body2>-</Body2>

                            {/* Status */}
                            {ticket.sale_status === TICKET_STATUS.live && (
                                <Chip label={"Live"} color={"successSm"} />
                            )}
                            {ticket.sale_status === TICKET_STATUS.saleEnded && (
                                <Chip label={"Sale Ended"} color={"errorSm"} />
                            )}
                            {ticket.sale_status === TICKET_STATUS.soldOut && (
                                <Chip label={"Sold Out"} color={"errorSm"} />
                            )}
                            {ticket.sale_status === TICKET_STATUS.commingSoon && (
                                <Chip label={"Coming Soon"} color={"warningSm"} />
                            )}
                            {ticket.sale_status === TICKET_STATUS.scheduled && (
                                <Chip label={"Scheduled"} color={"scheduledSm"} />
                            )}

                            {/* Actions */}
                            <div className="ticket-inventory-actions">
                                <IconButton
                                    onClick={(e) => onElipsisClick(e, ticket)}
                                    aria-controls={open ? "ticket-menu" : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? "true" : undefined}
                                >
                                    <MoreVert />
                                </IconButton>
                                <Menus
                                    id="ticket-menus"
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClick={onElipsisClose}
                                    onClose={onElipsisClose}
                                    menus={menusList}
                                    shouldDisabledMenus={false}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Pagination Footer */}
            {data.length > 0 && (
                <div className="ticket-inventory-footer">
                    <Body3>
                        Page: {currentPage} of {totalPages}
                    </Body3>
                    <div className="ticket-inventory-pagination">
                        <IconButton
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            sx={{
                                '&.Mui-disabled': {
                                    color: 'var(--quaternary-color)',
                                    opacity: '0.5',
                                    background: 'transparent'
                                }
                            }}
                        >
                            <ChevronLeft />
                        </IconButton>
                        <IconButton
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            sx={{
                                '&.Mui-disabled': {
                                    color: 'var(--quaternary-color)',
                                    opacity: '0.5',
                                    background: 'transparent'
                                }
                            }}
                        >
                            <ChevronRight />
                        </IconButton>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TicketInventory;